import { useEffect, useRef } from 'react'
import type { ChangelogItem } from './changelog.server'

const SPEED = 64
const NUDGE = 360

const Chevron = ({ dir }: { dir: -1 | 1 }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d={dir < 0 ? 'M14 6l-6 6 6 6' : 'M10 6l6 6-6 6'} />
  </svg>
)

/** A strip of recent developer-facing changelog lines that scrolls under the demos, at the page's end. It pauses on hover, nudges
 *  with the arrows, and holds still under reduced motion. Items are doubled so the loop is seamless. */
export const ChangelogTicker = ({ items }: { items: ChangelogItem[] }) => {
  const run = useRef<HTMLDivElement>(null)
  const paused = useRef(false)
  const nudge = useRef(0)

  useEffect(() => {
    const el = run.current
    if (!el) return
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    let half = el.scrollWidth / 2
    let x = 0
    let last: number | null = null
    let raf = 0
    const measure = () => {
      half = el.scrollWidth / 2
    }
    const place = () => {
      if (half) {
        while (x <= -half) x += half
        while (x > 0) x -= half
      }
      el.style.transform = `translateX(${x}px)`
    }
    const frame = (t: number) => {
      if (last === null) last = t
      const dt = Math.min(0.05, (t - last) / 1000)
      last = t
      if (nudge.current) {
        const step =
          Math.sign(nudge.current) * Math.min(Math.abs(nudge.current), 900 * dt)
        x += step
        nudge.current -= step
      } else if (!paused.current && !reduce) {
        x -= SPEED * dt
      }
      place()
      raf = requestAnimationFrame(frame)
    }
    window.addEventListener('resize', measure)
    raf = requestAnimationFrame(frame)
    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', measure)
    }
  }, [items])

  if (!items.length) return null
  const seq = [...items, ...items]

  return (
    <div
      className="ticker"
      aria-label="Recent changelog"
      onMouseEnter={() => (paused.current = true)}
      onMouseLeave={() => (paused.current = false)}
    >
      <a className="tk-label" href="https://docs.omni.co/changelog">
        Changelog
      </a>
      <button
        className="tk-btn"
        type="button"
        aria-label="Scroll back"
        onClick={() => (nudge.current += NUDGE)}
      >
        <Chevron dir={-1} />
      </button>
      <div className="tk-track">
        <div className="tk-run" ref={run}>
          {seq.map((item, i) => (
            <a className="tk-item" href={item.link} key={`${i}-${item.text}`}>
              <span className="tk-date">{item.date}</span>
              <span className="tk-tag">{item.tag}</span>
              {item.text}
            </a>
          ))}
        </div>
      </div>
      <button
        className="tk-btn"
        type="button"
        aria-label="Scroll forward"
        onClick={() => (nudge.current -= NUDGE)}
      >
        <Chevron dir={1} />
      </button>
    </div>
  )
}
